/**
 * waap-emergency-tab.tsx — Emergency Mitigation tab (B3 batch).
 *
 * "Break-glass" account-level config with three sub-strategies:
 *   - Kill Switch        (emergency.config.kill_switch)
 *   - Read-Only Website  (emergency.config.read_only_website)
 *   - Static Website     (emergency.config.static_website)
 *
 * Pattern: GET the full object → operator edits one sub-strategy → PUT full
 * object back (read-modify-write). Each sub-strategy has enable toggle,
 * scheduling type (always / once / periodic), and action list.
 *
 * Data sources:
 *   GET /services/:serviceId/waap/emergency
 *   PUT /services/:serviceId/waap/emergency
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import {
  Card, CardHeader, CardTitle, CardDescription, CardBody,
  Button, Checkbox, Input, Switch,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  Skeleton, Badge,
} from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { Field } from '@vnetwork/ui/forms';
import { Section, SettingRow } from '@vnetwork/ui/layout';
import { EmptyState } from '@vnetwork/ui/data';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { ActAsMutationConfirm } from '@/pages/operations/detail/cdn/act-as-mutation-confirm';
import { actAsClient } from '@/api/act-as-client';
import { actAsWAAPKeys } from '@/api/act-as-waap';
import {
  useActAsWAAPEmergency,
  EMERGENCY_SCHEDULING,
  EMERGENCY_ACTIONS,
  type EmergencyMitigation,
  type EmergencyMitigationConfig,
  type EmergencyStrategy,
} from '@/api/act-as-waap-account';

/* ── Constants ───────────────────────────────────────────────────────────── */

type StrategyKey = 'kill_switch' | 'read_only_website' | 'static_website';

const STRATEGY_META: { key: StrategyKey; labelKey: string; fallbackLabel: string; descKey: string; fallbackDesc: string }[] = [
  {
    key: 'kill_switch',
    labelKey: 'waap_domain.emergency_kill_switch',
    fallbackLabel: 'Kill Switch',
    descKey: 'waap_domain.emergency_kill_switch_desc',
    fallbackDesc: 'Immediately blocks all traffic for selected websites.',
  },
  {
    key: 'read_only_website',
    labelKey: 'waap_domain.emergency_readonly',
    fallbackLabel: 'Read-Only Website',
    descKey: 'waap_domain.emergency_readonly_desc',
    fallbackDesc: 'Allows GET/HEAD but blocks all write requests (POST, PUT, DELETE, PATCH).',
  },
  {
    key: 'static_website',
    labelKey: 'waap_domain.emergency_static',
    fallbackLabel: 'Static Website',
    descKey: 'waap_domain.emergency_static_desc',
    fallbackDesc: 'Serves a static HTML page for all requests (uses uploaded static files).',
  },
];

const SCHEDULE_OPTIONS = [
  { value: EMERGENCY_SCHEDULING.ALWAYS, labelKey: 'waap_domain.emergency_sched_always', fallback: 'Always on' },
  { value: EMERGENCY_SCHEDULING.ONCE,   labelKey: 'waap_domain.emergency_sched_once',   fallback: 'Once (time range)' },
  { value: EMERGENCY_SCHEDULING.REPEAT, labelKey: 'waap_domain.emergency_sched_repeat', fallback: 'Periodic (weekly)' },
];

const ACTION_OPTIONS = [
  { value: EMERGENCY_ACTIONS.BLOCK_CODE,    labelKey: 'waap_domain.emergency_action_block',    fallback: 'Block (HTTP status code)' },
  { value: EMERGENCY_ACTIONS.RESPONSE_HTML, labelKey: 'waap_domain.emergency_action_html',     fallback: 'Custom HTML response' },
];

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/* ── EmptyStrategy factory ───────────────────────────────────────────────── */

function emptyStrategy(): EmergencyStrategy {
  return {
    enable: false,
    timer_type: EMERGENCY_SCHEDULING.ALWAYS,
    action_list: [],
    sites: [],
  };
}

/* ── Sub-strategy editor ─────────────────────────────────────────────────── */

interface StrategyEditorProps {
  meta: (typeof STRATEGY_META)[number];
  strategy: EmergencyStrategy;
  onChange: (updated: EmergencyStrategy) => void;
}

function StrategyEditor({ meta, strategy, onChange }: StrategyEditorProps) {
  const { t } = useTranslation();

  function patch(key: keyof EmergencyStrategy, value: unknown) {
    onChange({ ...strategy, [key]: value });
  }

  function toggleAction(action: string) {
    const list = strategy.action_list ?? [];
    const next = list.includes(action) ? list.filter((a) => a !== action) : [...list, action];
    patch('action_list', next);
  }

  function toggleWeekday(day: number) {
    const days = strategy.timer_repeat_days ?? [];
    const next = days.includes(day) ? days.filter((d) => d !== day) : [...days, day];
    patch('timer_repeat_days', next);
  }

  const isRepeat = strategy.timer_type === EMERGENCY_SCHEDULING.REPEAT;
  const isOnce   = strategy.timer_type === EMERGENCY_SCHEDULING.ONCE;

  return (
    <Card>
      <CardHeader>
        <CardTitle style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
          <AlertTriangle size={15} style={{ color: 'var(--vn-warning-text)', flexShrink: 0 }} />
          {t(meta.labelKey, meta.fallbackLabel)}
          {strategy.enable && (
            <Badge tone="danger" style={{ marginLeft: 'var(--vn-space-2)' }}>
              {t('waap_domain.emergency_active', 'ACTIVE')}
            </Badge>
          )}
        </CardTitle>
        <CardDescription style={{ fontSize: 'var(--vn-font-size-xs)' }}>
          {t(meta.descKey, meta.fallbackDesc)}
        </CardDescription>
      </CardHeader>

      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        {/* Enable toggle */}
        <Section>
          <SettingRow
            title={t('waap_domain.emergency_enable', 'Enable')}
            control={
              <Switch
                checked={strategy.enable}
                onCheckedChange={(v) => patch('enable', v)}
              />
            }
          />
        </Section>

        {/* Scheduling */}
        <Field label={t('waap_domain.emergency_schedule', 'Schedule')}>
          {(props) => (
            <Select
              value={strategy.timer_type}
              onValueChange={(v) => patch('timer_type', v)}
            >
              <SelectTrigger {...props}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SCHEDULE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {t(o.labelKey, o.fallback)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>

        {/* Once: start + end datetime */}
        {isOnce && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-3)' }}>
            <Field label={t('waap_domain.emergency_start', 'Start')}>
              {(props) => (
                <Input
                  {...props}
                  type="datetime-local"
                  value={strategy.timer_once ?? ''}
                  onChange={(e) => patch('timer_once', e.target.value)}
                />
              )}
            </Field>
            <Field label={t('waap_domain.emergency_end', 'End')}>
              {(props) => (
                <Input
                  {...props}
                  type="datetime-local"
                  value={strategy.timer_end ?? ''}
                  onChange={(e) => patch('timer_end', e.target.value)}
                />
              )}
            </Field>
          </div>
        )}

        {/* Repeat: daily window + weekday picker */}
        {isRepeat && (
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-3)' }}>
              <Field label={t('waap_domain.emergency_repeat_start', 'Daily from')}>
                {(props) => (
                  <Input
                    {...props}
                    type="time"
                    value={strategy.timer_repeat_start ?? ''}
                    onChange={(e) => patch('timer_repeat_start', e.target.value)}
                  />
                )}
              </Field>
              <Field label={t('waap_domain.emergency_repeat_end', 'Daily until')}>
                {(props) => (
                  <Input
                    {...props}
                    type="time"
                    value={strategy.timer_repeat_end ?? ''}
                    onChange={(e) => patch('timer_repeat_end', e.target.value)}
                  />
                )}
              </Field>
            </div>
            {/* Weekday checkboxes */}
            <div style={{ display: 'flex', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
              {WEEKDAY_LABELS.map((label, idx) => {
                const active = (strategy.timer_repeat_days ?? []).includes(idx);
                return (
                  <Button
                    key={idx}
                    type="button"
                    size="sm"
                    variant={active ? 'primary' : 'secondary'}
                    onClick={() => toggleWeekday(idx)}
                  >
                    {label}
                  </Button>
                );
              })}
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          <span style={{ fontSize: 'var(--vn-font-size-xs)', fontWeight: 500, color: 'var(--vn-fg-muted)' }}>
            {t('waap_domain.emergency_actions', 'Actions')}
          </span>
          <div style={{ display: 'flex', gap: 'var(--vn-space-3)', flexWrap: 'wrap' }}>
            {ACTION_OPTIONS.map((opt) => {
              const checked = (strategy.action_list ?? []).includes(opt.value);
              return (
                <label
                  key={opt.value}
                  style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', fontSize: 'var(--vn-font-size-sm)', cursor: 'pointer' }}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggleAction(opt.value)}
                  />
                  {t(opt.labelKey, opt.fallback)}
                </label>
              );
            })}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

/* ── Root component ──────────────────────────────────────────────────────── */

interface WaapEmergencyTabProps {
  serviceId: string;
}

/**
 * Emergency Mitigation tab.
 *
 * Loads the full EmergencyMitigation object, lets the operator edit any of the
 * three sub-strategies, then PUT the whole object back via ActAsMutationConfirm.
 */
export function WaapEmergencyTab({ serviceId }: WaapEmergencyTabProps) {
  const { t } = useTranslation();
  const emergencyQ = useActAsWAAPEmergency(serviceId);

  // Local copy of the full config object being edited.
  const [draftConfig, setDraftConfig] = useState<EmergencyMitigationConfig | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const server = emergencyQ.data;

  // Seed draft on first successful load.
  useEffect(() => {
    if (server && draftConfig === null) {
      setDraftConfig(server.config ?? {});
    }
  }, [server, draftConfig]);

  if (emergencyQ.isLoading) return <TabLoading rows={3} />;
  if (emergencyQ.isError) {
    return (
      <TabError
        title={t('waap_domain.emergency_load_error', 'Failed to load emergency config')}
        onRetry={() => emergencyQ.refetch()}
      />
    );
  }

  const config = draftConfig ?? {};
  const serverConfig = server?.config ?? {};
  const isDirty = JSON.stringify(config) !== JSON.stringify(serverConfig);

  function updateStrategy(key: StrategyKey, updated: EmergencyStrategy) {
    setDraftConfig((prev) => ({ ...(prev ?? {}), [key]: updated }));
  }

  function getStrategy(key: StrategyKey): EmergencyStrategy {
    return config[key] ?? emptyStrategy();
  }

  // Build PUT payload: merge edited config back into the full object.
  function buildPayload(): EmergencyMitigation {
    return { ...(server ?? { config: {} }), config };
  }

  if (!server && !emergencyQ.isLoading) {
    return (
      <EmptyState
        title={t('waap_domain.emergency_not_configured', 'Emergency mitigation not configured')}
        description={t(
          'waap_domain.emergency_not_configured_desc',
          'No emergency mitigation configuration found for this service.',
        )}
      />
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
      <Alert tone="danger" style={{ fontSize: 'var(--vn-font-size-xs)' }}>
        {t(
          'waap_domain.emergency_warning',
          'Emergency mitigation is a break-glass control. Enabling Kill Switch will drop all traffic immediately. Proceed with caution.',
        )}
      </Alert>

      {emergencyQ.isLoading || draftConfig === null ? (
        <Skeleton style={{ width: '100%', height: 200 }} />
      ) : (
        <>
          {STRATEGY_META.map((meta) => (
            <StrategyEditor
              key={meta.key}
              meta={meta}
              strategy={getStrategy(meta.key)}
              onChange={(updated) => updateStrategy(meta.key, updated)}
            />
          ))}
        </>
      )}

      {/* Save bar */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--vn-space-3)' }}>
        <Button
          size="sm"
          variant="secondary"
          disabled={!isDirty}
          onClick={() => {
            setDraftConfig(serverConfig);
          }}
        >
          {t('waap_domain.emergency_discard', 'Discard changes')}
        </Button>
        <Button
          size="sm"
          variant="danger"
          disabled={!isDirty}
          onClick={() => setConfirmOpen(true)}
        >
          {t('waap_domain.emergency_save', 'Save Emergency Config')}
        </Button>
      </div>

      <ActAsMutationConfirm
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('waap_domain.emergency_confirm_title', 'Save Emergency Mitigation')}
        summary={t(
          'waap_domain.emergency_confirm_summary',
          'Update emergency mitigation settings for this WAAP service. Active strategies take effect immediately.',
        )}
        destructive
        queryKeysToInvalidate={[actAsWAAPKeys.emergency(serviceId)]}
        mutationFn={() =>
          actAsClient.put(`/services/${serviceId}/waap/emergency`, buildPayload())
        }
        onSuccess={() => {
          setConfirmOpen(false);
          // Reset draft so next open re-syncs from server.
          setDraftConfig(null);
          emergencyQ.refetch();
        }}
      />
    </div>
  );
}
